import { JsonTree } from "@magic-md/plugin-sdk";
import type { ParsedDocument } from "@magic-md/core";
import type { LocaleParams } from "@magic-md/i18n";
import { useT } from "@magic-md/i18n/react";
import {
    Activity,
    CheckCircle2,
    Circle,
    Cloud,
    Code2,
    Copy,
    Package,
    PanelLeftClose,
    PanelLeftOpen,
    PanelRightClose,
    PanelRightOpen,
    Pause,
    Play,
    Save,
    Server,
    Trash2,
    X,
    XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import type { ReactNode } from "react";
import {
    ContextMenu,
    type ContextMenuItem,
} from "../ContextMenu";
import { cn } from "../../lib/utils";
import {
    getDocumentTraceDirPath,
    traceRunVisibleInTracePanel,
} from "../../lib/trace-paths";
import { getPlatformService } from "../../platform";
import { useEditorStore } from "../../store/editor-store";
import {
    type TraceEventRecord,
    type TraceRun,
    type TraceSpan,
    ensureTraceNotificationHandlers,
    useTraceStore,
} from "../../store/trace-store";
import { pluginUIRegistry } from "../../plugins/plugin-ui-registry";

const DEFAULT_LEFT_WIDTH = 224;
const MIN_LEFT_WIDTH = 160;
const MAX_LEFT_WIDTH = 420;
const DEFAULT_RIGHT_WIDTH = 360;
const MIN_RIGHT_WIDTH = 260;
const MAX_RIGHT_WIDTH = 620;
const SPAN_GRID_TEMPLATE = "minmax(220px, 300px) 84px minmax(320px, 1fr)";
const BUNDLE_REFRESH_EVENT = "magic-md:bundle-refresh";

type EditorT = (key: string, params?: LocaleParams) => string;

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}

function parseTraceTime(value: string | undefined): number | null {
    if (!value) return null;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : null;
}

function formatMs(value: number | undefined): string {
    if (value == null) return "";
    if (value < 1000) return `${Math.round(value)}ms`;
    return `${(value / 1000).toFixed(2)}s`;
}

function formatClock(value: string): string {
    const date = new Date(value);
    return `${date.toLocaleTimeString("en-GB", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    })}.${String(date.getMilliseconds()).padStart(3, "0")}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

function attrString(attrs: Record<string, unknown> | undefined, key: string): string | undefined {
    const value = attrs?.[key];
    return typeof value === "string" ? value : undefined;
}

function isCloudSidecarSpan(span: TraceSpan): boolean {
    return (
        attrString(span.attrs, "sidecarSourceKind") === "cloud" ||
        attrString(span.attrs, "sidecarKind") === "cloud" ||
        attrString(span.attrs, "sidecarTransportType") === "cloud-worker-http"
    );
}

function cloudPluginSourceKind(span: TraceSpan): "cloud-worker" | "cloud-bundle" | undefined {
    const pluginName = span.pluginName ?? attrString(span.attrs, "pluginName");
    if (!pluginName) return undefined;
    const sourceKind = pluginUIRegistry.get(pluginName)?.source?.kind;
    return sourceKind === "cloud-worker" || sourceKind === "cloud-bundle"
        ? sourceKind
        : undefined;
}

function traceHasCloudSource(span: TraceSpan): boolean {
    return span.kind === "cloud-worker" || !!cloudPluginSourceKind(span) || isCloudSidecarSpan(span);
}

function traceCloudSourceTitle(span: TraceSpan, t: EditorT): string {
    const labels: string[] = [];
    const pluginSourceKind = cloudPluginSourceKind(span);
    if (span.kind === "cloud-worker") {
        labels.push(t("trace.cloudWorker"));
    } else if (pluginSourceKind === "cloud-worker") {
        labels.push(t("pluginSource.cloudWorker"));
    } else if (pluginSourceKind === "cloud-bundle") {
        labels.push(t("pluginSource.cloudBundle"));
    }
    if (isCloudSidecarSpan(span)) labels.push(t("trace.cloudSidecar"));
    return labels.join(" · ");
}

function hasDisplayValue(value: unknown): boolean {
    if (value == null) return false;
    if (typeof value === "string") return value.length > 0;
    return true;
}

function summarizeDetailValue(value: unknown): string {
    if (value == null) return "";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (Array.isArray(value)) return `${value.length} items`;
    if (isRecord(value)) {
        const type = typeof value.type === "string" ? `${value.type} · ` : "";
        return `${type}${Object.keys(value).length} fields`;
    }
    return String(value);
}

function statusClass(status: TraceSpan["status"]): string {
    switch (status) {
        case "completed":
            return "text-success";
        case "failed":
            return "text-destructive";
        case "cancelled":
            return "text-warning";
        case "skipped":
            return "text-muted-foreground";
        default:
            return "text-info";
    }
}

function traceEventToneClass(event: TraceEventRecord): string {
    const attrs = isRecord(event.attrs) ? event.attrs : {};
    const status = typeof attrs.status === "string" ? attrs.status : undefined;
    const state = typeof attrs.state === "string" ? attrs.state : undefined;
    const signature = `${event.type} ${status ?? ""} ${state ?? ""}`.toLowerCase();
    if (signature.includes("fail") || signature.includes("error")) return "border-destructive bg-destructive";
    if (signature.includes("cancel")) return "border-warning bg-warning";
    if (signature.includes("complete") || signature.includes("success")) return "border-success bg-success";
    if (signature.includes("start") || signature.includes("running")) return "border-info bg-info";
    return "border-info bg-info";
}

function traceEventTooltip(event: TraceEventRecord): string {
    return [
        formatClock(event.timestamp),
        event.type,
        event.message,
    ].filter(Boolean).join(" · ");
}

function StatusIcon({ status }: { status: TraceSpan["status"] }) {
    let icon: ReactNode;
    if (status === "completed") {
        icon = <CheckCircle2 size={12} className="text-success" />;
    } else if (status === "failed") {
        icon = <XCircle size={12} className="text-destructive" />;
    } else if (status === "cancelled") {
        icon = <Pause size={12} className="text-warning" />;
    } else {
        icon = <Circle size={12} className="text-info" />;
    }
    return (
        <span
            data-trace-status-icon
            className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center"
        >
            {icon}
        </span>
    );
}

type SpanDisplayRow = {
    span: TraceSpan;
    depth: number;
    rangeStartMs: number;
    rangeEndMs: number;
};

type SpanRange = {
    startMs: number;
    endMs: number;
};

function getSpanOwnRange(span: TraceSpan): SpanRange {
    const startMs = parseTraceTime(span.startedAt) ?? 0;
    const completedMs = parseTraceTime(span.completedAt);
    const fallbackEndMs = typeof span.durationMs === "number"
        ? startMs + Math.max(0, span.durationMs)
        : startMs;
    const endMs = Math.max(startMs, completedMs ?? fallbackEndMs);
    return { startMs, endMs };
}

function spanKindRank(kind: TraceSpan["kind"]): number {
    switch (kind) {
        case "job":
            return 0;
        case "unit":
            return 1;
        case "phase":
            return 2;
        case "sidecar-ensure":
            return 3;
        case "plugin-action":
        case "plugin-api":
            return 4;
        case "cloud-worker":
            return 5;
        case "sidecar-call":
            return 6;
        default:
            return 7;
    }
}

function findUnitSpanId(spans: TraceSpan[], unitId: string | undefined): string | undefined {
    if (!unitId) return undefined;
    return spans.find((span) => span.kind === "unit" && span.unitId === unitId)?.id;
}

function findPluginActionSpanId(spans: TraceSpan[], unitId: string | undefined): string | undefined {
    if (!unitId) return undefined;
    return spans.find((span) => span.kind === "plugin-action" && span.unitId === unitId)?.id;
}

function findPluginOperationSpanId(spans: TraceSpan[], unitId: string | undefined): string | undefined {
    if (!unitId) return undefined;
    return findPluginActionSpanId(spans, unitId) ??
        spans.find((span) => span.kind === "plugin-api" && span.unitId === unitId)?.id;
}

function buildCascadeParentByUnitId(run: TraceRun): Map<string, string> {
    const result = new Map<string, string>();
    for (const event of run.events) {
        if (event.type !== "execution.cascade" || !isRecord(event.attrs)) continue;
        const sourceUnitId = event.attrs.sourceUnitId;
        const targetUnitIds = event.attrs.targetUnitIds;
        if (typeof sourceUnitId !== "string" || !Array.isArray(targetUnitIds)) continue;
        for (const targetUnitId of targetUnitIds) {
            if (typeof targetUnitId !== "string" || targetUnitId === sourceUnitId) continue;
            result.set(targetUnitId, sourceUnitId);
        }
    }
    return result;
}

function normalizeSpanParentId(
    span: TraceSpan,
    spans: TraceSpan[],
    byId: Map<string, TraceSpan>,
    rootJobId: string | undefined,
    cascadeParentByUnitId: Map<string, string>,
): string | undefined {
    if (span.kind === "job") return undefined;
    const explicitParent = span.parentId && byId.has(span.parentId) ? span.parentId : undefined;
    const unitSpanId = findUnitSpanId(spans, span.unitId);
    if (span.kind === "unit") {
        const cascadeParentUnitId = span.unitId ? cascadeParentByUnitId.get(span.unitId) : undefined;
        const cascadeParentSpanId = findUnitSpanId(spans, cascadeParentUnitId);
        return cascadeParentSpanId ?? explicitParent ?? rootJobId;
    }
    if (span.kind === "sidecar-call") {
        const explicitParentSpan = explicitParent ? byId.get(explicitParent) : undefined;
        if (explicitParentSpan && explicitParentSpan.kind !== "unit") return explicitParent;
        return findPluginOperationSpanId(spans, span.unitId) ?? explicitParent ?? unitSpanId ?? rootJobId;
    }
    if (span.kind === "phase" || span.kind === "sidecar-ensure" || span.kind === "plugin-action") {
        return explicitParent ?? unitSpanId ?? rootJobId;
    }
    return explicitParent ?? unitSpanId ?? rootJobId;
}

function buildSpanDisplayRows(run: TraceRun): SpanDisplayRow[] {
    const spans = run.spans;
    const byId = new Map(spans.map((span) => [span.id, span]));
    const originalIndex = new Map(spans.map((span, index) => [span.id, index]));
    const rootJobId = spans.find((span) => span.kind === "job")?.id;
    const cascadeParentByUnitId = buildCascadeParentByUnitId(run);
    const parentById = new Map<string, string | undefined>();
    for (const span of spans) {
        const parentId = normalizeSpanParentId(span, spans, byId, rootJobId, cascadeParentByUnitId);
        parentById.set(span.id, parentId === span.id ? undefined : parentId);
    }

    const childrenByParent = new Map<string, TraceSpan[]>();
    const roots: TraceSpan[] = [];
    for (const span of spans) {
        const parentId = parentById.get(span.id);
        if (parentId && byId.has(parentId)) {
            const children = childrenByParent.get(parentId) ?? [];
            children.push(span);
            childrenByParent.set(parentId, children);
        } else {
            roots.push(span);
        }
    }

    const rangeCache = new Map<string, SpanRange>();
    const spanRange = (span: TraceSpan): SpanRange => {
        const cached = rangeCache.get(span.id);
        if (cached) return cached;
        const own = getSpanOwnRange(span);
        let startMs = own.startMs;
        let endMs = own.endMs;
        for (const child of childrenByParent.get(span.id) ?? []) {
            const childRange = spanRange(child);
            startMs = Math.min(startMs, childRange.startMs);
            endMs = Math.max(endMs, childRange.endMs);
        }
        const value = { startMs, endMs };
        rangeCache.set(span.id, value);
        return value;
    };
    const compare = (a: TraceSpan, b: TraceSpan): number => {
        const rankDelta = spanKindRank(a.kind) - spanKindRank(b.kind);
        if (rankDelta !== 0) return rankDelta;
        const startDelta = spanRange(a).startMs - spanRange(b).startMs;
        if (startDelta !== 0) return startDelta;
        return (originalIndex.get(a.id) ?? 0) - (originalIndex.get(b.id) ?? 0);
    };
    for (const children of childrenByParent.values()) {
        children.sort(compare);
    }
    roots.sort(compare);

    const rows: SpanDisplayRow[] = [];
    const visiting = new Set<string>();
    const visited = new Set<string>();
    const visit = (span: TraceSpan, depth: number) => {
        if (visiting.has(span.id) || visited.has(span.id)) return;
        visiting.add(span.id);
        const range = spanRange(span);
        rows.push({
            span,
            depth,
            rangeStartMs: range.startMs,
            rangeEndMs: range.endMs,
        });
        for (const child of childrenByParent.get(span.id) ?? []) {
            visit(child, depth + 1);
        }
        visiting.delete(span.id);
        visited.add(span.id);
    };
    for (const root of roots) visit(root, 0);
    return rows;
}

function findLineInNodes(nodes: unknown[] | undefined, unitId: string): number | null {
    if (!Array.isArray(nodes)) return null;
    for (const node of nodes) {
        const record = node as Record<string, unknown>;
        if (record.type === "definition" && record.id === unitId) {
            const source = record.source as { range?: { startLine?: number } } | undefined;
            if (typeof source?.range?.startLine === "number") return source.range.startLine;
        }
        if (record.type === "for-block" && record.id === unitId) {
            const source = record.source as { range?: { startLine?: number } } | undefined;
            if (typeof source?.range?.startLine === "number") return source.range.startLine;
        }
        if (record.type === "content-definition") {
            const definition = record.definition as {
                id?: string;
                range?: { start?: number };
            } | undefined;
            if (definition?.id === unitId && typeof definition.range?.start === "number") {
                return definition.range.start;
            }
        }
        const childLine = findLineInNodes(record.children as unknown[] | undefined, unitId);
        if (childLine != null) return childLine;
    }
    return null;
}

function findUnitSourceLine(parsed: ParsedDocument | null | undefined, unitId: string | undefined): number | null {
    if (!parsed || !unitId) return null;
    const blockLine = findLineInNodes(parsed.blocks as unknown[] | undefined, unitId);
    if (blockLine != null) return blockLine;
    const treeLine = findLineInNodes((parsed as ParsedDocument & { tree?: unknown[] }).tree, unitId);
    if (treeLine != null) return treeLine;
    const contentRange = parsed.contentDefinitions?.get(unitId)?.range;
    return typeof contentRange?.start === "number" ? contentRange.start : null;
}

function getParsedDocumentForTraceRun(run: TraceRun): ParsedDocument | null {
    const state = useEditorStore.getState();
    const tab = run.filePath
        ? state.tabs.find((item) => item.filePath === run.filePath)
        : state.tabs.find((item) => item.id === state.activeTabId);
    if (!tab) return state.parsedDocument;
    return state.parsedDocumentMap.get(tab.id) ??
        (tab.id === state.activeTabId ? state.parsedDocument : null);
}

async function gotoTraceUnitDefinition(run: TraceRun, span: TraceSpan): Promise<void> {
    if (!run.filePath || !span.unitId) return;
    const parsed = getParsedDocumentForTraceRun(run);
    const line = findUnitSourceLine(parsed, span.unitId);
    if (line == null) return;
    const editor = useEditorStore.getState();
    await editor.openFileFromPath(run.filePath);
    useEditorStore.getState().setCursorPosition(line, 1);
    window.dispatchEvent(
        new CustomEvent("magic-md:goto-line", {
            detail: { line, flash: true },
        }),
    );
}

function getTraceFilePath(run: TraceRun): string | null {
    if (!run.filePath) return null;
    return `${getDocumentTraceDirPath(run.filePath)}/${encodeURIComponent(run.traceId)}.mmtrace`;
}

async function saveTraceRun(run: TraceRun, t: EditorT): Promise<void> {
    const tracePath = getTraceFilePath(run);
    if (!tracePath) {
        useEditorStore.getState().setStatusMessage(t("trace.saveFailedMissingDocument"), 4000);
        return;
    }
    const traceDir = tracePath.slice(0, tracePath.lastIndexOf("/"));
    try {
        const engine = getPlatformService().engine;
        await engine.workspaceMkdir({ path: traceDir, recursive: true });
        await engine.workspaceWriteFile({
            path: tracePath,
            content: JSON.stringify(run, null, 2),
        });
        window.dispatchEvent(new CustomEvent(BUNDLE_REFRESH_EVENT, {
            detail: {
                filePath: tracePath,
                traceDir,
                bundleDir: traceDir.replace(/\/traces$/, ""),
            },
        }));
        useEditorStore.getState().setStatusMessage(t("trace.saved", { traceId: run.traceId }), 4000);
    } catch (error) {
        const message = error instanceof Error ? error.message : "Unknown error";
        useEditorStore.getState().setStatusMessage(t("trace.saveFailed", { message }), 4000);
    }
}

async function deleteTraceRun(run: TraceRun, t: EditorT): Promise<void> {
    const ok = window.confirm(t("trace.deleteConfirm", { traceId: run.traceId }));
    if (!ok) return;
    const tracePath = getTraceFilePath(run);
    useTraceStore.getState().deleteTrace(run.traceId);
    if (!tracePath) {
        useEditorStore.getState().setStatusMessage(t("trace.deleted", { traceId: run.traceId }), 4000);
        return;
    }
    const traceDir = tracePath.slice(0, tracePath.lastIndexOf("/"));
    try {
        await getPlatformService().engine.workspaceRemove({
            path: tracePath,
            recursive: false,
        });
    } catch {
        // The run may only exist in memory until explicitly saved.
    }
    window.dispatchEvent(new CustomEvent(BUNDLE_REFRESH_EVENT, {
        detail: {
            filePath: tracePath,
            traceDir,
            bundleDir: traceDir.replace(/\/traces$/, ""),
        },
    }));
    useEditorStore.getState().setStatusMessage(t("trace.deleted", { traceId: run.traceId }), 4000);
}

function RunList({
    runs,
    selectedTraceId,
    onSelect,
    onSave,
    onDelete,
    width,
    onCollapse,
}: {
    runs: TraceRun[];
    selectedTraceId: string | null;
    onSelect: (traceId: string) => void;
    onSave: (run: TraceRun) => void;
    onDelete: (run: TraceRun) => void;
    width: number;
    onCollapse: () => void;
}) {
    const t = useT("editor");
    const [runMenu, setRunMenu] = useState<{
        open: boolean;
        x: number;
        y: number;
        run: TraceRun | null;
    }>({ open: false, x: 0, y: 0, run: null });
    const selectedMenuRun = runMenu.run;
    const tracePath = selectedMenuRun ? getTraceFilePath(selectedMenuRun) : null;
    const menuItems = useMemo<ContextMenuItem[]>(() => {
        const run = selectedMenuRun;
        if (!run) return [];
        return [
            {
                key: "save-trace",
                label: t("trace.save"),
                icon: <Save size={13} />,
                disabled: !tracePath,
                onClick: () => onSave(run),
            },
            {
                key: "copy-trace-json",
                label: t("trace.copyJson"),
                icon: <Copy size={13} />,
                onClick: () => void copyText(JSON.stringify(run, null, 2)),
            },
            {
                key: "copy-trace-path",
                label: t("trace.copyPath"),
                icon: <Copy size={13} />,
                disabled: !tracePath,
                onClick: () => {
                    if (tracePath) void copyText(tracePath);
                },
            },
            {
                key: "delete-trace",
                label: t("trace.delete"),
                icon: <Trash2 size={13} />,
                separator: true,
                onClick: () => onDelete(run),
            },
        ];
    }, [onDelete, onSave, selectedMenuRun, t, tracePath]);

    return (
        <div
            className="shrink-0 border-r border-border bg-muted/10"
            style={{ width }}
        >
            <div className="flex h-full min-w-0 flex-col">
                <div className="flex h-7 shrink-0 items-center gap-1 border-b border-border/40 px-2 text-[10px]">
                    <span className="font-medium text-muted-foreground">{t("trace.runs")}</span>
                    <button
                        type="button"
                        className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                        title={t("trace.collapseRuns")}
                        onClick={onCollapse}
                    >
                        <PanelLeftClose size={12} />
                    </button>
                </div>
                <div className="min-h-0 flex-1 overflow-auto py-1">
                {runs.map((run) => (
                    <button
                        key={run.traceId}
                        type="button"
                        onClick={() => onSelect(run.traceId)}
                        onContextMenu={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onSelect(run.traceId);
                            setRunMenu({
                                open: true,
                                x: event.clientX,
                                y: event.clientY,
                                run,
                            });
                        }}
                        className={cn(
                            "mx-1 flex min-h-12 w-[calc(100%-0.5rem)] flex-col rounded px-2 py-1 text-left text-[10px] transition-colors",
                            selectedTraceId === run.traceId
                                ? "bg-primary/10 text-foreground"
                                : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                        )}
                    >
                        <span className="flex min-w-0 items-center gap-1">
                            <StatusIcon status={run.status} />
                            <span className="truncate font-medium">
                                {run.target === "all" ? t("trace.runAll") : run.unitId ?? t("trace.runUnit")}
                            </span>
                        </span>
                        <span className="mt-0.5 truncate font-mono text-[9px] opacity-70">
                            {formatClock(run.startedAt)} · {formatMs(run.durationMs) || t("trace.running")}
                        </span>
                    </button>
                ))}
                {runs.length === 0 ? (
                    <div className="flex h-full items-center justify-center px-3 text-center text-[11px] text-muted-foreground/40">
                        {t("trace.noTraces")}
                    </div>
                ) : null}
                </div>
            </div>
            <ContextMenu
                open={runMenu.open}
                x={runMenu.x}
                y={runMenu.y}
                items={menuItems}
                onClose={() => setRunMenu((state) => ({ ...state, open: false }))}
            />
        </div>
    );
}

function SpanRows({
    run,
    selectedSpanId,
    onSelect,
}: {
    run: TraceRun;
    selectedSpanId: string | null;
    onSelect: (spanId: string) => void;
}) {
    const t = useT("editor");
    const [spanMenu, setSpanMenu] = useState<{
        open: boolean;
        x: number;
        y: number;
        span: TraceSpan | null;
    }>({ open: false, x: 0, y: 0, span: null });
    const rows = useMemo(() => buildSpanDisplayRows(run), [run]);
    const eventsBySpanId = useMemo(() => {
        const map = new Map<string, TraceRun["events"]>();
        for (const event of run.events) {
            if (!event.spanId) continue;
            const events = map.get(event.spanId) ?? [];
            events.push(event);
            map.set(event.spanId, events);
        }
        return map;
    }, [run.events]);
    const eventTimes = useMemo(() => (
        run.events
            .map((event) => parseTraceTime(event.timestamp))
            .filter((time): time is number => time != null)
    ), [run.events]);
    const runStartMs = parseTraceTime(run.startedAt) ?? 0;
    const runCompletedMs = parseTraceTime(run.completedAt);
    const runFallbackEndMs = typeof run.durationMs === "number"
        ? runStartMs + Math.max(0, run.durationMs)
        : runStartMs;
    const startMs = Math.min(runStartMs, ...rows.map((row) => row.rangeStartMs), ...eventTimes);
    const endMs = Math.max(
        runCompletedMs ?? runFallbackEndMs,
        ...rows.map((row) => row.rangeEndMs),
        ...eventTimes,
        startMs + 1,
    );
    const totalMs = Math.max(1, endMs - startMs);
    const selectedMenuSpan = spanMenu.span;
    const sourceLine = selectedMenuSpan
        ? findUnitSourceLine(getParsedDocumentForTraceRun(run), selectedMenuSpan.unitId)
        : null;
    const menuItems = useMemo<ContextMenuItem[]>(() => {
        const span = selectedMenuSpan;
        if (!span) return [];
        return [
            {
                key: "goto-definition",
                label: sourceLine != null
                    ? t("trace.gotoDefinitionLine", { line: sourceLine })
                    : t("trace.gotoDefinition"),
                icon: <Code2 size={13} />,
                disabled: !run.filePath || !span.unitId || sourceLine == null,
                onClick: () => void gotoTraceUnitDefinition(run, span),
            },
            {
                key: "copy-span",
                label: t("trace.copySpanJson"),
                icon: <Copy size={13} />,
                onClick: () => void copyText(JSON.stringify(span, null, 2)),
            },
            {
                key: "copy-trace",
                label: t("trace.copyJson"),
                icon: <Copy size={13} />,
                separator: true,
                onClick: () => void copyText(JSON.stringify(run, null, 2)),
            },
        ];
    }, [run, selectedMenuSpan, sourceLine, t]);

    return (
        <div className="min-w-0 flex-1 overflow-auto">
            <div
                className="grid border-b border-border/40 bg-muted/10 px-2 py-1 text-[9px] uppercase text-muted-foreground/60"
                style={{ gridTemplateColumns: SPAN_GRID_TEMPLATE, minWidth: 620 }}
            >
                <span>{t("trace.span")}</span>
                <span className="px-3">{t("trace.duration")}</span>
                <span>{t("trace.waterfall")}</span>
            </div>
            {rows.map(({ span, depth, rangeStartMs, rangeEndMs }) => {
                const visualDurationMs = Math.max(0, rangeEndMs - rangeStartMs);
                const left = Math.max(0, ((rangeStartMs - startMs) / totalMs) * 100);
                const width = Math.max(
                    span.status === "running" ? 1.5 : 0.8,
                    (visualDurationMs / totalMs) * 100,
                );
                const spanEvents = eventsBySpanId.get(span.id) ?? [];
                const cloudSourceTitle = traceCloudSourceTitle(span, t);
                return (
                    <button
                        key={span.id}
                        data-trace-span-id={span.id}
                        data-trace-depth={depth}
                        data-trace-range-start-ms={rangeStartMs}
                        data-trace-range-end-ms={rangeEndMs}
                        type="button"
                        onClick={() => onSelect(span.id)}
                        onContextMenu={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onSelect(span.id);
                            setSpanMenu({
                                open: true,
                                x: event.clientX,
                                y: event.clientY,
                                span,
                            });
                        }}
                        className={cn(
                            "grid h-7 w-full items-center border-b border-border/20 px-2 text-left text-[11px] transition-colors",
                            selectedSpanId === span.id
                                ? "bg-primary/10"
                                : "hover:bg-muted/30",
                        )}
                        style={{ gridTemplateColumns: SPAN_GRID_TEMPLATE, minWidth: 620 }}
                    >
                        <span className="flex min-w-0 w-full items-center gap-1.5" style={{ paddingLeft: depth * 12 }}>
                            <StatusIcon status={span.status} />
                            <span className="min-w-0 truncate font-medium">{span.name}</span>
                            <span className="ml-auto flex shrink-0 items-center gap-1">
                                {traceHasCloudSource(span) ? (
                                    <span
                                        data-trace-source-icon="cloud"
                                        title={cloudSourceTitle}
                                        aria-label={cloudSourceTitle}
                                        className="inline-flex h-4 w-4 items-center justify-center rounded text-primary"
                                    >
                                        <Cloud size={10} />
                                    </span>
                                ) : null}
                                <span
                                    data-trace-kind-badge={span.kind}
                                    className="rounded bg-muted px-1 py-px font-mono text-[9px] text-muted-foreground"
                                >
                                    {span.kind}
                                </span>
                            </span>
                        </span>
                        <span className={cn("px-3 font-mono text-[10px]", statusClass(span.status))}>
                            {formatMs(visualDurationMs) || (span.status === "running" ? t("trace.running") : "")}
                        </span>
                        <span className="relative h-3 rounded bg-muted/60">
                            <span
                                data-trace-waterfall-bar
                                className={cn(
                                    "absolute top-0.5 h-2 rounded-sm",
                                    span.status === "failed"
                                        ? "bg-destructive"
                                        : span.status === "cancelled"
                                            ? "bg-warning"
                                            : span.status === "running"
                                                ? "bg-info animate-pulse"
                                                : "bg-success",
                                )}
                                style={{
                                    left: `${Math.min(left, 98)}%`,
                                    width: `${Math.min(width, 100 - Math.min(left, 98))}%`,
                                }}
                            />
                            {spanEvents.map((event) => {
                                const eventMs = parseTraceTime(event.timestamp);
                                if (eventMs == null) return null;
                                const eventLeft = clamp(((eventMs - startMs) / totalMs) * 100, 0, 100);
                                const eventTooltip = traceEventTooltip(event);
                                return (
                                    <span
                                        key={event.id}
                                        data-trace-waterfall-event
                                        data-trace-event-id={event.id}
                                        title={eventTooltip}
                                        aria-label={eventTooltip}
                                        className="group/event-marker absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 cursor-help"
                                        style={{ left: `${eventLeft}%` }}
                                    >
                                        <span
                                            className={cn(
                                                "absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full border shadow-sm ring-2 ring-background",
                                                traceEventToneClass(event),
                                            )}
                                        />
                                        <span
                                            data-trace-waterfall-event-tooltip
                                            className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1 hidden min-w-40 max-w-72 -translate-x-1/2 whitespace-normal rounded border border-border bg-popover px-2 py-1 text-left text-[10px] leading-snug text-popover-foreground shadow-lg group-hover/event-marker:block"
                                        >
                                            <span className="block font-mono text-[9px] text-muted-foreground">
                                                {formatClock(event.timestamp)}
                                            </span>
                                            <span className="block truncate font-medium">
                                                {event.type}
                                            </span>
                                            {event.message ? (
                                                <span className="mt-0.5 block text-muted-foreground">
                                                    {event.message}
                                                </span>
                                            ) : null}
                                        </span>
                                    </span>
                                );
                            })}
                        </span>
                    </button>
                );
            })}
            {rows.length === 0 ? (
                <div className="flex h-24 items-center justify-center text-[11px] text-muted-foreground/40">
                    {t("trace.waitingEvents")}
                </div>
            ) : null}
            <ContextMenu
                open={spanMenu.open}
                x={spanMenu.x}
                y={spanMenu.y}
                items={menuItems}
                onClose={() => setSpanMenu((state) => ({ ...state, open: false }))}
            />
        </div>
    );
}

type DetailTab = "details" | "events" | "raw";

type PayloadSection = {
    key: string;
    title: string;
    value: unknown;
};

type EntityCardModel = {
    title: string;
    subtitle?: string;
    icon: ReactNode;
};

function buildEntityCard(run: TraceRun, span: TraceSpan | null, t: EditorT): EntityCardModel {
    if (!span) {
        return {
            title: run.target === "all" ? t("trace.runAll") : t("trace.runUnitWithId", { unitId: run.unitId ?? "unit" }),
            subtitle: run.traceId,
            icon: <Activity size={18} />,
        };
    }

    if (span.kind === "cloud-worker") {
        const transport = attrString(span.attrs, "transport");
        const direction = attrString(span.attrs, "direction");
        const pluginName = span.pluginName ?? attrString(span.attrs, "pluginName");
        const actionName = span.actionName ?? attrString(span.attrs, "actionName");
        return {
            title: pluginName ?? t("trace.cloudWorker"),
            subtitle: [
                t("trace.cloudWorker"),
                transport,
                direction,
                actionName ? `${pluginName ?? "cloud"}.${actionName}` : undefined,
            ].filter(Boolean).join(" · "),
            icon: <Cloud size={18} />,
        };
    }

    const pluginName = span.pluginName ?? attrString(span.attrs, "pluginName");
    const actionName = span.actionName ?? attrString(span.attrs, "actionName");
    if (pluginName) {
        const manifest = pluginUIRegistry.get(pluginName)?.manifest;
        return {
            title: manifest?.name ?? pluginName,
            subtitle: actionName ? `${pluginName}.${actionName}` : pluginName,
            icon: manifest?.icon ? (
                <span className="text-lg leading-none">{manifest.icon}</span>
            ) : (
                <Package size={18} />
            ),
        };
    }

    if (span.sidecarId) {
        const isCloudSidecar = isCloudSidecarSpan(span);
        const transport = attrString(span.attrs, "sidecarTransportType");
        return {
            title: span.sidecarId,
            subtitle: [
                isCloudSidecar ? t("trace.cloudSidecar") : undefined,
                transport,
                span.method,
            ].filter(Boolean).join(" · "),
            icon: isCloudSidecar ? <Cloud size={18} /> : <Server size={18} />,
        };
    }

    if (span.kind.startsWith("extension-")) {
        return {
            title: span.extensionId ?? attrString(span.attrs, "extensionId") ?? span.name,
            subtitle: [
                t("trace.extension"),
                span.method,
                span.name,
            ].filter(Boolean).join(" · "),
            icon: <Code2 size={18} />,
        };
    }

    return {
        title: span.name,
        subtitle: span.unitId,
        icon: <Activity size={18} />,
    };
}

function EntityCard({ model }: { model: EntityCardModel }) {
    return (
        <div className="rounded border border-border/50 bg-background/70 p-2">
            <div className="flex min-w-0 items-start gap-2">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded border border-border/40 bg-muted/40 text-muted-foreground">
                    {model.icon}
                </div>
                <div className="min-w-0 flex-1 self-center">
                    <div className="truncate text-[12px] font-semibold">
                        {model.title}
                    </div>
                    {model.subtitle ? (
                        <div className="truncate font-mono text-[9px] text-muted-foreground">
                            {model.subtitle}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

function firstRecordValue(records: Array<Record<string, unknown> | null>, keys: string[]): unknown {
    for (const record of records) {
        if (!record) continue;
        for (const key of keys) {
            const value = record[key];
            if (hasDisplayValue(value)) return value;
        }
    }
    return undefined;
}

function pickKnownFields(record: Record<string, unknown> | null, keys: string[]): Record<string, unknown> | null {
    if (!record) return null;
    const picked: Record<string, unknown> = {};
    for (const key of keys) {
        if (hasDisplayValue(record[key])) picked[key] = record[key];
    }
    return Object.keys(picked).length > 0 ? picked : null;
}

function buildPayloadSections(data: TraceRun | TraceSpan, t: EditorT): PayloadSection[] {
    const attrsValue = (data as TraceSpan).attrs;
    const attrs = isRecord(attrsValue) ? attrsValue : null;
    const outputValue = attrs?.output;
    const resultValue = attrs?.result;
    const paramsValue = attrs?.params;
    const output = isRecord(outputValue) ? outputValue : null;
    const result = isRecord(resultValue) ? resultValue : null;
    const params = isRecord(paramsValue) ? paramsValue : null;
    const span = "kind" in data ? data as TraceSpan : null;
    const records: Array<Record<string, unknown> | null> = [attrs, output, result, params];
    const isPluginAction = span?.kind === "plugin-action";
    const isSidecarCall = span?.kind === "sidecar-call";
    const isCloudWorker = span?.kind === "cloud-worker";
    const isExtensionSpan = span?.kind.startsWith("extension-") ?? false;
    const sections: PayloadSection[] = [];
    const usedKeys = new Set<string>();

    const add = (key: string, title: string, value: unknown) => {
        if (!hasDisplayValue(value) || usedKeys.has(key)) return;
        usedKeys.add(key);
        sections.push({ key, title, value });
    };

    add("plugin", t("trace.plugin"), pickKnownFields({
        pluginName: span?.pluginName ?? attrs?.pluginName,
        actionName: span?.actionName ?? attrs?.actionName,
        timeoutSecs: attrs?.timeoutSecs,
        message: attrs?.message,
    }, ["pluginName", "actionName", "timeoutSecs", "message"]));
    add("sidecar", t("trace.sidecar"), pickKnownFields({
        sidecarId: span?.sidecarId,
        kind: attrs?.sidecarKind,
        sourceKind: attrs?.sidecarSourceKind,
        transportType: attrs?.sidecarTransportType,
        method: span?.method,
        command: attrs?.command,
        capabilities: attrs?.capabilities,
        available: attrs?.available,
        timeoutSecs: attrs?.timeoutSecs,
    }, ["sidecarId", "kind", "sourceKind", "transportType", "method", "command", "capabilities", "available", "timeoutSecs"]));
    if (isCloudWorker) {
        add("cloud-worker", t("trace.cloudWorker"), pickKnownFields({
            transport: attrs?.transport,
            direction: attrs?.direction,
            endpoint: attrs?.endpoint,
            requestId: attrs?.requestId,
            callId: attrs?.callId,
            ok: attrs?.ok,
            declaredBackends: attrs?.declaredBackends,
        }, ["transport", "direction", "endpoint", "requestId", "callId", "ok", "declaredBackends"]));
    }
    if (isExtensionSpan) {
        add("extension", t("trace.extension"), pickKnownFields({
            extensionId: span?.extensionId,
            kind: span?.kind,
            method: span?.method,
            operation: attrs?.operation,
            capability: attrs?.capability,
            toolName: attrs?.toolName,
            event: attrs?.event,
            key: attrs?.key,
            prefix: attrs?.prefix,
            timeoutSec: attrs?.timeoutSec,
        }, ["extensionId", "kind", "method", "operation", "capability", "toolName", "event", "key", "prefix", "timeoutSec"]));
        add("params", t("trace.params"), attrs?.params ?? attrs?.args ?? pickKnownFields({
            payload: attrs?.payload,
            settings: attrs?.settings,
            valueType: attrs?.valueType,
        }, ["payload", "settings", "valueType"]));
        add("result", t("trace.result"), attrs?.result);
    }
    if (isPluginAction) {
        add("plugin-input", t("trace.pluginInput"), attrs?.input ?? pickKnownFields({
            config: attrs?.config,
            actionParams: attrs?.actionParams,
            previousOutput: attrs?.previousOutput,
        }, ["config", "actionParams", "previousOutput"]));
        add("plugin-result", t("trace.pluginResult"), attrs?.result ?? attrs?.output);
    }
    if (isSidecarCall) {
        add("sidecar-params", t("trace.sidecarParams"), attrs?.params);
        add("sidecar-result", t("trace.sidecarResult"), attrs?.result);
    }
    add("config", t("trace.resolvedConfig"), attrs?.config);
    if (!isSidecarCall) add("params", t("trace.params"), attrs?.params);
    add(
        "request",
        t("trace.request"),
        firstRecordValue(records, ["request", "req"]) ??
            pickKnownFields(params ?? output ?? attrs ?? null, ["method", "url", "headers", "query", "body"]),
    );
    add(
        "response",
        t("trace.response"),
        firstRecordValue(records, ["response", "res"]) ??
            pickKnownFields(result ?? output ?? attrs ?? null, ["status", "statusCode", "headers", "body", "data"]),
    );
    if (!isSidecarCall && !isPluginAction) add("result", t("trace.result"), attrs?.result);
    if (!isPluginAction) add("output", t("trace.output"), attrs?.output);
    add("error", t("trace.error"), (data as TraceSpan).error ?? attrs?.error ?? output?.__error);
    return sections;
}

function PayloadSectionView({ section }: { section: PayloadSection }) {
    return (
        <div className="rounded border border-border/40 bg-background/60">
            <div className="border-b border-border/30 px-2 py-1 text-[9px] uppercase text-muted-foreground/60">
                {section.title}
            </div>
            <div className="max-h-56 overflow-auto p-2 text-[10px]">
                {isRecord(section.value) || Array.isArray(section.value) ? (
                    <JsonTree value={section.value} defaultExpandDepth={2} />
                ) : (
                    <pre className="whitespace-pre-wrap break-words font-mono text-[10px] leading-relaxed">
                        {summarizeDetailValue(section.value)}
                    </pre>
                )}
            </div>
        </div>
    );
}

function EventList({ events, t }: { events: TraceRun["events"]; t: EditorT }) {
    if (events.length === 0) {
        return (
            <div className="flex h-24 items-center justify-center text-[11px] text-muted-foreground/40">
                {t("trace.noEvents")}
            </div>
        );
    }
    return (
        <div className="space-y-2">
            {events.map((event) => (
                <div
                    key={event.id}
                    className="rounded border border-border/40 bg-background/60 text-[10px]"
                >
                    <div className="flex items-center gap-2 border-b border-border/30 px-2 py-1">
                        <span className="font-mono text-muted-foreground">
                            {formatClock(event.timestamp)}
                        </span>
                        <span className="min-w-0 truncate font-medium">{event.type}</span>
                    </div>
                    {event.message ? (
                        <div className="px-2 py-1 text-muted-foreground">
                            {event.message}
                        </div>
                    ) : null}
                    {event.attrs ? (
                        <div className="max-h-44 overflow-auto border-t border-border/30 p-2">
                            <JsonTree value={event.attrs} defaultExpandDepth={1} />
                        </div>
                    ) : null}
                </div>
            ))}
        </div>
    );
}

function DetailPane({
    run,
    spanId,
    width,
    onCollapse,
    onClose,
}: {
    run: TraceRun;
    spanId: string | null;
    width: number;
    onCollapse: () => void;
    onClose: () => void;
}) {
    const t = useT("editor");
    const span = spanId ? run.spans.find((item) => item.id === spanId) ?? null : null;
    const events = span
        ? run.events.filter((event) => event.spanId === span.id)
        : run.events.slice(-50);
    const data = span ?? run;
    const [activeTab, setActiveTab] = useState<DetailTab>("details");
    const entityCard = useMemo(() => buildEntityCard(run, span, t), [run, span, t]);
    const sections = useMemo(() => buildPayloadSections(data, t), [data, t]);

    useEffect(() => {
        setActiveTab("details");
    }, [run.traceId, spanId]);

    return (
        <div
            className="shrink-0 overflow-hidden border-l border-border bg-muted/10"
            style={{ width }}
        >
            <div className="flex h-full flex-col">
                <div className="flex h-9 shrink-0 items-center gap-2 border-b border-border/40 px-2 py-1">
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-[11px] font-semibold">
                            {span ? span.name : run.traceId}
                        </div>
                        <div className="truncate font-mono text-[9px] text-muted-foreground">
                            {span ? span.id : run.executionId}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                        title={t("trace.collapseDetails")}
                        onClick={onCollapse}
                    >
                        <PanelRightClose size={12} />
                    </button>
                    <button
                        type="button"
                        className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                        title={t("trace.closeDetails")}
                        onClick={onClose}
                    >
                        <X size={12} />
                    </button>
                </div>
                <div
                    role="tablist"
                    className="flex h-8 shrink-0 items-center gap-1 border-b border-border/40 px-2"
                >
                    {(["details", "events", "raw"] as const).map((tab) => (
                        <button
                            key={tab}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === tab}
                            className={cn(
                                "h-6 rounded px-2 text-[10px] capitalize transition-colors",
                                activeTab === tab
                                    ? "bg-primary/10 text-foreground"
                                    : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                            )}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab === "details" ? t("trace.details") : tab === "events" ? t("trace.events") : t("trace.raw")}
                        </button>
                    ))}
                </div>
                <div className="min-h-0 flex-1 overflow-auto p-2">
                    {activeTab === "details" ? (
                        <div className="space-y-2">
                            <EntityCard model={entityCard} />
                            {sections.length > 0 ? (
                                sections.map((section) => (
                                    <PayloadSectionView
                                        key={section.key}
                                        section={section}
                                    />
                                ))
                            ) : (
                                <div className="rounded border border-border/40 bg-background/60 p-2 text-[11px] text-muted-foreground/50">
                                    {t("trace.noStructuredPayload")}
                                </div>
                            )}
                        </div>
                    ) : null}
                    {activeTab === "events" ? <EventList events={events} t={t} /> : null}
                    {activeTab === "raw" ? <JsonTree value={data} defaultExpandDepth={1} /> : null}
                </div>
            </div>
        </div>
    );
}

async function copyText(value: string): Promise<void> {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(value);
    }
}

function ResizeHandle({
    onMouseDown,
    label,
}: {
    onMouseDown: (event: ReactMouseEvent<HTMLDivElement>) => void;
    label: string;
}) {
    return (
        <div
            role="separator"
            aria-label={label}
            aria-orientation="vertical"
            className="w-1 shrink-0 cursor-col-resize bg-transparent transition-colors hover:bg-primary/20 active:bg-primary/30"
            onMouseDown={onMouseDown}
        />
    );
}

export function TracePanel() {
    const t = useT("editor");

    useEffect(() => {
        ensureTraceNotificationHandlers();
    }, []);

    const runs = useTraceStore((state) => state.runs);
    const selectedTraceId = useTraceStore((state) => state.selectedTraceId);
    const selectTrace = useTraceStore((state) => state.selectTrace);
    const recording = useTraceStore((state) => state.recording);
    const setRecording = useTraceStore((state) => state.setRecording);
    const clear = useTraceStore((state) => state.clear);
    const exportTrace = useTraceStore((state) => state.exportTrace);
    const activeDocumentPath = useEditorStore((state) => {
        const activeTab = state.tabs.find((tab) => tab.id === state.activeTabId);
        return activeTab?.filePath;
    });
    const visibleRuns = useMemo(
        () => activeDocumentPath
            ? runs.filter((run) => traceRunVisibleInTracePanel(run, activeDocumentPath))
            : runs,
        [activeDocumentPath, runs],
    );
    const selectedRun = visibleRuns.find((run) => run.traceId === selectedTraceId) ?? visibleRuns[0] ?? null;
    const [selectedSpanId, setSelectedSpanId] = useState<string | null>(null);
    const [leftWidth, setLeftWidth] = useState(DEFAULT_LEFT_WIDTH);
    const [rightWidth, setRightWidth] = useState(DEFAULT_RIGHT_WIDTH);
    const [leftCollapsed, setLeftCollapsed] = useState(false);
    const [rightCollapsed, setRightCollapsed] = useState(false);
    const [rightVisible, setRightVisible] = useState(true);

    useEffect(() => {
        if (selectedRun?.traceId === selectedTraceId) return;
        selectTrace(selectedRun?.traceId ?? null);
        setSelectedSpanId(null);
    }, [selectTrace, selectedRun?.traceId, selectedTraceId]);

    const handleSelectTrace = (traceId: string) => {
        selectTrace(traceId);
        setSelectedSpanId(null);
        setRightVisible(true);
        setRightCollapsed(false);
    };

    const handleSelectSpan = (spanId: string) => {
        setSelectedSpanId(spanId);
        setRightVisible(true);
        setRightCollapsed(false);
    };

    const handleExport = () => {
        if (!selectedRun) return;
        const text = exportTrace(selectedRun.traceId);
        if (text) void copyText(text);
    };

    const handleLeftResize = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        const startX = event.clientX;
        const startWidth = leftWidth;
        const onMove = (moveEvent: MouseEvent) => {
            setLeftWidth(clamp(startWidth + moveEvent.clientX - startX, MIN_LEFT_WIDTH, MAX_LEFT_WIDTH));
        };
        const onUp = () => {
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
        };
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
    }, [leftWidth]);

    const handleRightResize = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        const startX = event.clientX;
        const startWidth = rightWidth;
        const onMove = (moveEvent: MouseEvent) => {
            setRightWidth(clamp(startWidth - (moveEvent.clientX - startX), MIN_RIGHT_WIDTH, MAX_RIGHT_WIDTH));
        };
        const onUp = () => {
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
        };
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
    }, [rightWidth]);

    return (
        <div className="flex h-full min-h-0 min-w-0 flex-col">
            <div className="flex h-8 shrink-0 items-center gap-1 border-b border-border/40 px-2 text-[11px]">
                <Activity size={13} className="text-muted-foreground" />
                <span className="font-medium">{t("trace.title")}</span>
                {selectedRun ? (
                    <span className="ml-2 truncate font-mono text-[10px] text-muted-foreground">
                        {selectedRun.traceId}
                    </span>
                ) : null}
                <div className="ml-auto flex items-center gap-1">
                    <button
                        type="button"
                        className={cn(
                            "inline-flex h-6 items-center gap-1 rounded px-2 text-[10px] transition-colors",
                            recording
                                ? "bg-success/10 text-success"
                                : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                        )}
                        onClick={() => setRecording(!recording)}
                    >
                        {recording ? <Pause size={11} /> : <Play size={11} />}
                        {recording ? t("trace.recording") : t("trace.start")}
                    </button>
                    <button
                        type="button"
                        className="inline-flex h-6 items-center gap-1 rounded px-2 text-[10px] text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                        onClick={handleExport}
                        disabled={!selectedRun}
                    >
                        <Copy size={11} />
                        {t("trace.copyJsonShort")}
                    </button>
                    <button
                        type="button"
                        className="inline-flex h-6 items-center gap-1 rounded px-2 text-[10px] text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                        onClick={clear}
                    >
                        <Trash2 size={11} />
                        {t("trace.clear")}
                    </button>
                </div>
            </div>
            <div className="flex min-h-0 min-w-0 flex-1">
                {leftCollapsed ? (
                    <div className="flex w-9 shrink-0 flex-col items-center border-r border-border bg-muted/10 py-1">
                        <button
                            type="button"
                            className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                            title={t("trace.showRuns")}
                            onClick={() => setLeftCollapsed(false)}
                        >
                            <PanelLeftOpen size={13} />
                        </button>
                    </div>
                ) : (
                    <>
                        <RunList
                            runs={visibleRuns}
                            selectedTraceId={selectedRun?.traceId ?? null}
                            onSelect={handleSelectTrace}
                            onSave={(run) => void saveTraceRun(run, t)}
                            onDelete={(run) => void deleteTraceRun(run, t)}
                            width={leftWidth}
                            onCollapse={() => setLeftCollapsed(true)}
                        />
                        <ResizeHandle
                            label={t("trace.resizeRuns")}
                            onMouseDown={handleLeftResize}
                        />
                    </>
                )}
                {selectedRun ? (
                    <>
                        <SpanRows
                            run={selectedRun}
                            selectedSpanId={selectedSpanId}
                            onSelect={handleSelectSpan}
                        />
                        {rightVisible ? (
                            rightCollapsed ? (
                                <div className="flex w-9 shrink-0 flex-col items-center gap-1 border-l border-border bg-muted/10 py-1">
                                    <button
                                        type="button"
                                        className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                                        title={t("trace.showDetails")}
                                        onClick={() => setRightCollapsed(false)}
                                    >
                                        <PanelRightOpen size={13} />
                                    </button>
                                    <button
                                        type="button"
                                        className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                                        title={t("trace.closeDetails")}
                                        onClick={() => setRightVisible(false)}
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <ResizeHandle
                                        label={t("trace.resizeDetails")}
                                        onMouseDown={handleRightResize}
                                    />
                                    <DetailPane
                                        run={selectedRun}
                                        spanId={selectedSpanId}
                                        width={rightWidth}
                                        onCollapse={() => setRightCollapsed(true)}
                                        onClose={() => setRightVisible(false)}
                                    />
                                </>
                            )
                        ) : null}
                    </>
                ) : (
                    <div className="flex flex-1 items-center justify-center text-[11px] text-muted-foreground/40">
                        {t("trace.empty")}
                    </div>
                )}
            </div>
        </div>
    );
}
